import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Label, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Transition } from 'semantic-ui-react';
import { ThemeContext } from 'styled-components';
import CustomIcon from '~/components/CustomIcon';
import InfoPopup from '~/components/InfoPopup';
import { useAuth } from '~/routes/components/AuthProvider';
import { capitalizeFirstLetter, formatNumber, formatNumberWithoutFractions, getPrefixData } from '~/utils/helpers';
import { ChartContainer, TooltipWrapper } from './style';
import { useParams } from 'react-router-dom';
import { TradingActivitieSettingsType, TradingActivitieType } from '~/api/services/reporting';
import { FixedPricePrefixType } from '~/api/services/dashboard';

interface TradingChartProps {
  tradingActivityData: TradingActivitieType[]
  tradingSettings: TradingActivitieSettingsType
}

class CustomizedAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, stroke, payload, fill } = this.props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor='end' fill={fill} transform='rotate(-45)'>
          {payload.value}
        </text>
      </g>
    );
  }
}

class CustomizedYAxisTick extends React.Component {
  render() {
    //@ts-ignore
    const { x, y, stroke, payload, fill } = this.props;
    const numberOfSpacesBetweenLabelAndGraph = (parseInt(payload.value) / 1000).toString().length * 10 + 2
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={numberOfSpacesBetweenLabelAndGraph < 40 ? 40 : numberOfSpacesBetweenLabelAndGraph}
          y={0} dy={4} textAnchor='end' fill={fill} transform='rotate(0)'>
          {(parseInt(payload.value) / 1000) < 1 ? `${formatNumberWithoutFractions(parseInt(payload.value))}` : `${formatNumberWithoutFractions(parseInt(payload.value) / 1000)}k`}
        </text>
      </g>
    );
  }
}

const TradingChart: React.FC<TradingChartProps> = (props) => {
  const { tradingActivityData, tradingSettings } = props
  const { t } = useTranslation()
  const { prefix } = useParams()
  const { systemData: { amountUnit, amountPrice, amountVolume } } = useAuth()
  const { firstColor } = useContext(ThemeContext)
  const [activeIndex, setActiveIndex] = useState(0)
  const [dataLoading, setDataLoading] = useState(false)
  const chartData = tradingActivityData?.length > 12 ? splitEvery(tradingActivityData, 12) : [tradingActivityData]

  const prefixData = getPrefixData(prefix)

  const isFatProteinModule = prefix === FixedPricePrefixType.FatProtein
  const priceLabel = isFatProteinModule ? '€/100kg' : `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`

  useEffect(() => {
    setActiveIndex(0)
  }, [tradingActivityData])

  function splitEvery(array, length) {
    return array.reduce(
      (result, item, index) => {
        if (index % length === 0) result.push([])
        result[Math.floor(index / length)].push(item)
        return result
      }, [])
  }

  useEffect(() => {
    setDataLoading(false)
  }, [activeIndex])

  const data = chartData[activeIndex] ? chartData[activeIndex].map((item) => {
    return {
      ...item,
      avgPrice: item.avgPrice ? Number(item.avgPrice).toFixed(2) : null,
      avgFatPrice: item.avgFatPrice ? Number(item.avgFatPrice).toFixed(2) : null,
      avgProteinPrice: item.avgProteinPrice ? Number(item.avgProteinPrice).toFixed(2) : null,
      offeredAmount: item.offeredAmount,
      month: capitalizeFirstLetter(dayjs(item.month).format('MMM YYYY')),
      maxAmount: parseInt(item.maxAmount),
      remainingAmount: parseInt(item.remainingAmount),
      confirmedAmount: parseInt(item.confirmedAmount)
    }
  }) : []

  const firstMonth = chartData[activeIndex] && chartData[activeIndex].length !== 0 ? dayjs(chartData[activeIndex][0].month).format('MMM YYYY') : ''
  const lastMonth = chartData[activeIndex] && chartData[activeIndex].length !== 0 ? dayjs(chartData[activeIndex][chartData[activeIndex].length - 1].month).format('MMM YYYY') : ''

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const confirmedAmount = payload.find(item => item.dataKey === 'confirmedAmount')
      const remainingAmount = payload.find(item => item.dataKey === 'remainingAmount')
      const offeredAmount = formatNumberWithoutFractions(payload[0].payload.offeredAmount)
      const maxAmount = confirmedAmount.value + remainingAmount.value
      const maxPrice = formatNumber(payload[0].payload.maxPrice)
      const minPrice = formatNumber(payload[0].payload.minPrice)
      const avgFatPrice = formatNumber(payload[0].payload?.avgFatPrice)
      const avgProteinPrice = formatNumber(payload[0].payload?.avgProteinPrice)
      const percentAmount = formatNumber(confirmedAmount.value / parseFloat(maxAmount) * 100)
      return <TooltipWrapper>
        <div style={{ padding: 5 }} key={label}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Procurement month: {{label}} ({{percentAmount}}%)', { label, percentAmount })}</p>
        </div>
        {payload.map(item => {
          const value = item.unit && item.unit === amountUnit ? formatNumberWithoutFractions(item.value) : formatNumber(item.value)
          return <div style={{ padding: 5 }} key={item.name}>
            <p className='tooltipTitle' style={{ color: item.color }}>{`${item.name}: ${value} ${item.unit || ''}`}</p>
          </div>
        })}
        {isFatProteinModule && <>
          <div style={{ padding: 5 }} key={'avgFatPrice'}>
            <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.avgFatPrice}: ${avgFatPrice} ${priceLabel}`}</p>
          </div>
          <div style={{ padding: 5 }} key={'avgProteinPrice'}>
            <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.avgProteinPrice}: ${avgProteinPrice} ${priceLabel}`}</p>
          </div>
        </>}
        <div style={{ padding: 5 }} key={'max-amount'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Max amount: {{maxAmount}} {{amountUnit}}',
            { maxAmount: formatNumberWithoutFractions(maxAmount), amountUnit })}</p>
        </div>
        <div style={{ padding: 5 }} key={'offered-amount'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{t('Offered amount: {{offeredAmount}} {{amountUnit}}',
            { offeredAmount, amountUnit })}</p>
        </div>
        <div style={{ padding: 5 }} key={'max-price'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.max}: ${maxPrice} ${priceLabel}`}</p>
        </div>
        <div style={{ padding: 5 }} key={'min-price'}>
          <p className='tooltipTitle' style={{ color: 'black' }}>{`${prefixData.min}: ${minPrice} ${priceLabel}`}</p>
        </div>
      </TooltipWrapper>
    }

    return null;
  };

  const domainWithoutNullValues = data.map(item => item.avgPrice ? item : null).filter(item => !!item)
  const domainWithoutNullValuesBase = data.map(item => item.basePrice ? item : null).filter(item => !!item)
  const minAvg = Math.min(...domainWithoutNullValues.map(item => item.avgPrice))
  const maxAvg = Math.max(...domainWithoutNullValues.map(item => item.avgPrice))
  const minBase = Math.min(...domainWithoutNullValuesBase.map(item => item.basePrice))
  const maxBase = Math.max(...domainWithoutNullValuesBase.map(item => item.basePrice))
  const min = minAvg < minBase ? minAvg : minBase
  const max = maxBase > maxAvg ? maxBase : maxAvg

  return <ChartContainer>
    <div className='header'>
      {chartData?.length !== 1 && <div className='arrow-left'>
        <CustomIcon
          onClick={() => {
            setDataLoading(true)
            setActiveIndex(activeIndex - 1)
          }}
          disabled={activeIndex === 0}
          name='arrow alternate circle left'
        />
      </div>
      }
      <div className='title'>
        {t('Hedging activity {{firstMonth}} - {{lastMonth}}', { firstMonth: capitalizeFirstLetter(firstMonth), lastMonth: capitalizeFirstLetter(lastMonth) })}
        <InfoPopup
          content={prefixData?.tradingDetailInfo || t('Period of time of trading activities which are displayed in the chart and the table underneath. \n\
      You can define the period of time individually (search filter).')}
          styleIcon={{ cursor: 'pointer', fontSize: '1.0em', marginLeft: 5 }}
        />
      </div>
      {chartData?.length !== 1 && <div className='arrow-right'>
        <CustomIcon
          onClick={() => {
            setDataLoading(true)
            setActiveIndex(activeIndex + 1)
          }}
          disabled={activeIndex === chartData?.length - 1}
          name='arrow alternate circle right'
        />
      </div>
      }
    </div>
    <Transition visible={!dataLoading} animation='fade right' duration={250}>
      <ResponsiveContainer width={'100%'} height='80%'>
        <ComposedChart
          data={data}
          maxBarSize={50}
          margin={{
            top: 5,
            right: 30,
            left: 30,
            bottom: 5
          }}
        >
          <CartesianGrid strokeDasharray='1 1' />
          <XAxis
            height={65}
            tick={<CustomizedAxisTick />}
            dataKey='month'
          />
          <YAxis
            tickCount={6}
            domain={[Math.floor(min) - 5 < 0 ? 0 : Math.floor(min) - 5, Math.floor(max) + 5]}
            orientation='left'
            tickFormatter={(value) => formatNumber(value)}
            yAxisId='left'
            dataKey={'avgPrice'}
          >
            <Label
              value={t('Price (in {{priceLabel}})', { priceLabel })}
              angle={-90}
              position='insideLeft'
              offset={-5}
              style={{ textAnchor: 'middle' }} />
          </YAxis>
          <YAxis
            yAxisId='right'
            unit={'k'}
            tick={<CustomizedYAxisTick />}
            dataKey='maxAmount'
            orientation='right'
          >
            <Label
              value={t('Volume (in {{amountUnit}})', { amountUnit })}
              offset={2}
              position={{
                x: 80,
                y: 60
              }}
              angle={-90} />
          </YAxis>
          <Tooltip content={<CustomTooltip />} animationEasing={'ease-out'} />
          <Bar
            unit={amountUnit}
            yAxisId={'right'}
            dataKey='confirmedAmount'
            name={prefixData.amount}
            stackId='a'
            fill={firstColor}
          />
          <Bar
            unit={amountUnit}
            yAxisId={'right'}
            dataKey='remainingAmount'
            stackId='a'
            name={t('Remaining amount')}
            fill='#82ca9d'
          />
          <Line
            yAxisId={'left'}
            type='monotone'
            name={prefixData.average}
            dataKey={'avgPrice'}
            unit={priceLabel}
            stroke={firstColor}
            dot={{ r: 5 }}
            activeDot={{ r: 8 }}
          />
          <Line
            yAxisId={'left'}
            type='monotone'
            name={`${tradingSettings?.translation}`}
            unit={priceLabel}
            dataKey={'basePrice'}
            stroke={'black'}
            dot={{ r: 5 }}
            activeDot={{ r: 8 }}
          />
        </ComposedChart >
      </ResponsiveContainer>
    </Transition>
  </ChartContainer >
}
export default TradingChart